import { Controller } from "@hotwired/stimulus"

// Powers the Contracts list page: the New Contract popup, filter tabs,
// search, row → workspace navigation, and row-menu actions (toasts).
export default class extends Controller {
  static targets = ["newModal", "dirHint", "sendingSources", "receivingBlock", "row", "filterTab", "search", "empty",
                    "deleteModal", "deleteName", "deleteConfirmBtn", "deleteNotify", "sourceField", "templateField",
                    "uploadZone", "uploadPrompt", "uploadChip", "uploadError", "pdfInput", "uploadedPdfField",
                    "createBtn", "tableBody",
                    "trashSection", "trashToggle", "trashCount", "trashBody", "trashRow", "trashEmpty", "totalCount",
                    "deleteForeverModal", "deleteForeverName", "deleteForeverBtn",
                    "templatesBody", "templatesEmpty", "templateRow",
                    "newTemplateModal", "templateName", "templateType", "templateDescription", "templateSubmitBtn",
                    "renameTemplateModal", "renameTemplateName",
                    "deleteTemplateModal", "deleteTemplateName",
                    "aiBidZone", "aiBidPrompt", "aiBidChip", "aiBidInput", "counterpartyInput", "counterpartyResults",
                    "nameInput", "typeSelect", "aiPeekStatus", "retainageWrap", "directionField", "rxActionField",
                    "returnEmailField",
                    "dupModal", "dupNewName", "dupExistingName", "dupExistingMeta", "dupCompanyField", "dupForceField"]
  static values = { deleteUrlTemplate: String, presignUrl: String, peekBidUrl: String,
                    createTemplateUrl: String, renameTemplateUrl: String,
                    duplicateTemplateUrl: String, deleteTemplateUrl: String }

  connect() {
    // Returning via the browser Back button restores the page from the
    // bfcache with the submit button still in its loading state — reset it.
    this._onPageshow = () => this._setCreating(false)
    window.addEventListener("pageshow", this._onPageshow)
  }

  disconnect() {
    window.removeEventListener("pageshow", this._onPageshow)
  }

  // ── New Contract popup ──
  openNew(e) {
    if (e) e.preventDefault()
    this.newModalTarget.classList.remove("hidden")
  }
  closeNew() {
    this.newModalTarget.classList.add("hidden")
    this._setCreating(false)
  }
  backdropClose(e) { if (e.target === e.currentTarget) this.closeNew() }
  stop(e) { e.stopPropagation() }

  setDir(e) {
    const sending = e.currentTarget.dataset.dir === "sending"
    this._dir = sending ? "sending" : "receiving"
    if (this.hasDirectionFieldTarget) this.directionFieldTarget.value = this._dir
    this.element.querySelectorAll("[data-dir-tab]").forEach(b => this._tab(b, b.dataset.dir === (sending ? "sending" : "receiving")))
    this.dirHintTarget.textContent = sending
      ? "You're creating a contract to send out."
      : "You received this contract — upload it, then choose what to do."
    this.sendingSourcesTargets.forEach(el => el.classList.toggle("hidden", !sending))
    this.receivingBlockTarget.classList.toggle("hidden", sending)
    // Re-entering Receiving resets the choice to the default card — the
    // hidden field used to keep a stale pick while the cards showed
    // "Just store it" selected (Phelipe 2026-08-26 flow rework).
    if (!sending && this.hasRxActionFieldTarget) {
      this.rxActionFieldTarget.value = "store"
      this.element.querySelectorAll("[data-upload-opt]").forEach(l => {
        const selected = l.dataset.rxAction === "store"
        l.classList.toggle("border-brand", selected)
        l.classList.toggle("bg-brand/5", selected)
        l.classList.toggle("border-gray-200", !selected)
        const radio = l.querySelector("input[type=radio]")
        if (radio) radio.checked = selected
      })
    }
    this._clearUploadError()
    this._paintCreateBtn()
  }

  // ── Counterparty type-to-search (Phelipe 2026-07-29): the company's sub
  // database, same endpoint the Sub Payments add form uses. ──
  searchCounterparty() {
    clearTimeout(this._cpTimer)
    const q = this.counterpartyInputTarget.value.trim()
    if (q.length < 2) {
      this.counterpartyResultsTarget.classList.add("hidden")
      return
    }
    this._cpTimer = setTimeout(async () => {
      try {
        const url = this.counterpartyInputTarget.dataset.searchUrl
        const resp = await fetch(`${url}?q=${encodeURIComponent(q)}`, { headers: { "Accept": "application/json" } })
        const data = await resp.json()
        this._renderCounterpartyResults(data.subs || [])
      } catch (_) { /* quiet — search is a convenience */ }
    }, 250)
  }

  _renderCounterpartyResults(subs) {
    const box = this.counterpartyResultsTarget
    box.innerHTML = ""
    if (!subs.length) {
      box.classList.add("hidden")
      return
    }
    subs.slice(0, 8).forEach((sub) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 flex items-center justify-between gap-2"
      const name = document.createElement("span")
      name.className = "font-medium text-gray-800 truncate"
      name.textContent = sub.name
      const trade = document.createElement("span")
      trade.className = "text-gray-400 shrink-0"
      trade.textContent = sub.trade || ""
      item.append(name, trade)
      item.addEventListener("mousedown", (e) => {
        e.preventDefault()
        this.counterpartyInputTarget.value = sub.name
        box.classList.add("hidden")
      })
      box.appendChild(item)
    })
    box.classList.remove("hidden")
  }

  hideCounterpartyResultsSoon() {
    setTimeout(() => {
      if (this.hasCounterpartyResultsTarget) this.counterpartyResultsTarget.classList.add("hidden")
    }, 180)
  }

  // ── AI bid box (Phelipe 2026-07-29): the dropped file rides the form as
  // contract[ai_bid]; AI drafts from it server-side during create. ──
  browseAiBid() { this.aiBidInputTarget.click() }

  dragOverAiBid(e) {
    e.preventDefault()
    this.aiBidZoneTarget.classList.add("border-purple-500", "bg-purple-200")
  }

  dragLeaveAiBid() { this.aiBidZoneTarget.classList.remove("border-purple-500", "bg-purple-200") }

  dropAiBid(e) {
    e.preventDefault()
    this.dragLeaveAiBid()
    const files = Array.from(e.dataTransfer?.files || []).slice(0, 3)
    if (!files.length) return
    const dt = new DataTransfer()
    files.forEach(f => dt.items.add(f))
    this.aiBidInputTarget.files = dt.files
    this._showAiBidChip(files.map(f => f.name).join("  ·  "))
    this._peekAiBid(files[0])
  }

  aiBidChosen(e) {
    const files = Array.from(e.target.files || []).slice(0, 3)
    if (!files.length) return
    // Cap at 3 — quietly trim anything beyond (the input keeps them all,
    // so rebuild it with just the kept files).
    if ((e.target.files || []).length > 3) {
      const dt = new DataTransfer()
      files.forEach(f => dt.items.add(f))
      e.target.files = dt.files
    }
    this._showAiBidChip(files.map(f => f.name).join("  ·  "))
    this._peekAiBid(files[0])
  }

  // ── Bid peek (Phelipe 2026-08-04): the moment a bid lands in the AI box,
  // the server skims it and suggests the contract name, type, and
  // counterparty so the fields below fill themselves. Convenience only —
  // failures stay quiet and never block the popup. ──
  _peekAiBid(file, mode = null) {
    if (!file || !this.hasPeekBidUrlValue || !this.peekBidUrlValue) return
    const seq = (this._peekSeq = (this._peekSeq || 0) + 1)
    this._peekStatus("reading")
    this._setPeekingBtn(true)
    const fd = new FormData()
    fd.append("file", file)
    if (mode) fd.append("mode", mode)
    fetch(this.peekBidUrlValue, {
      method: "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
      credentials: "same-origin",
      body: fd
    }).then(resp => resp.ok ? resp.json() : null)
      .then(data => {
        if (seq !== this._peekSeq) return // a newer drop (or removal) superseded this
        this._setPeekingBtn(false)
        if (!data || !data.ok) { this._peekStatus(null); return }
        const filled = this._applyPeek(data)
        this._peekStatus(filled ? "filled" : null)
        if (filled) setTimeout(() => { if (seq === this._peekSeq) this._peekStatus(null) }, 6000)
      })
      .catch(() => {
        if (seq !== this._peekSeq) return
        this._setPeekingBtn(false)
        this._peekStatus(null)
      })
  }

  _cancelPeekAiBid() {
    this._peekSeq = (this._peekSeq || 0) + 1
    this._peekStatus(null)
    this._setPeekingBtn(false)
  }

  // While AI reads the dropped bid, Create & open waits: spinner + disabled
  // (Phelipe 2026-08-04) — restored the moment the peek resolves.
  _setPeekingBtn(on) {
    this._peeking = !!on
    if (!this.hasCreateBtnTarget) return
    const btn = this.createBtnTarget
    if (on) {
      if (this._peekBtnHtml == null) this._peekBtnHtml = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> ${this._peekBtnHtml}`
    } else {
      btn.disabled = false
      if (this._peekBtnHtml != null) { btn.innerHTML = this._peekBtnHtml; this._peekBtnHtml = null }
      // The saved HTML may predate an option change made mid-peek.
      this._paintCreateBtn()
    }
  }

  // The submit button reads as the ACTION the choice above will take —
  // "Store contract" / "Open to sign & send back" / "Open to send for
  // signature" — and Create & open for the Sending tab (Phelipe 2026-08-27).
  _paintCreateBtn() {
    if (!this.hasCreateBtnTarget || this._peeking) return
    const btn = this.createBtnTarget
    let label = btn.dataset.labelSending
    // Sending → Upload opens the PDF to place the signature spots next
    // (Phelipe 2026-10-23) — say so instead of "Create & open".
    const source = this.hasSourceFieldTarget ? this.sourceFieldTarget.value : "ai"
    if ((this._dir || "sending") === "sending" && source === "upload" && btn.dataset.labelUpload) {
      label = btn.dataset.labelUpload
    }
    if ((this._dir || "sending") === "receiving") {
      const rx = this.hasRxActionFieldTarget ? this.rxActionFieldTarget.value : "store"
      label = rx === "sign_back" ? btn.dataset.labelSignBack
        : rx === "another_party" ? btn.dataset.labelAnotherParty
        : btn.dataset.labelStore
    }
    if (label) btn.textContent = label
  }

  // Fill only what the user hasn't touched: the name while it still reads
  // the default (or our own last fill), the type unless they picked one
  // themselves, the counterparty while empty.
  _applyPeek(data) {
    let filled = false
    if (this.hasNameInputTarget && data.name) {
      const input = this.nameInputTarget
      const val = input.value.trim()
      if (!val || val === input.defaultValue || val === this._aiFilledName) {
        input.value = data.name
        this._aiFilledName = data.name
        this._flashField(input)
        filled = true
      }
    }
    if (this.hasTypeSelectTarget && data.contract_type && !this._userPickedType) {
      const select = this.typeSelectTarget
      if (select.value !== data.contract_type && [...select.options].some(o => o.value === data.contract_type)) {
        this._applyingPeek = true
        select.value = data.contract_type
        // Run the same template-memory swap a manual pick triggers.
        select.dispatchEvent(new Event("change", { bubbles: true }))
        this._applyingPeek = false
        this._flashField(select)
        filled = true
      }
    }
    if (this.hasCounterpartyInputTarget && data.counterparty) {
      const cp = this.counterpartyInputTarget
      const val = cp.value.trim()
      if (!val || val === this._aiFilledCounterparty) {
        cp.value = data.counterparty
        this._aiFilledCounterparty = data.counterparty
        this._flashField(cp)
        filled = true
      }
    }
    // The sender's email rides hidden into create — it prefills where
    // "sign & send back" returns the executed copy (Phelipe 2026-08-26).
    if (this.hasReturnEmailFieldTarget && data.sender_email) {
      this.returnEmailFieldTarget.value = data.sender_email
    }
    return filled
  }

  _flashField(el) {
    el.classList.add("ring-2", "ring-purple-300", "bg-purple-50")
    setTimeout(() => el.classList.remove("ring-2", "ring-purple-300", "bg-purple-50"), 1600)
  }

  _peekStatus(state) {
    // Two instances now — the sending AI-bid panel and the receiving
    // drop zone (Phelipe 2026-08-26) — keep both in step.
    this.aiPeekStatusTargets.forEach(el => {
      if (!state) { el.classList.add("hidden"); return }
      el.textContent = state === "reading"
        ? (el.dataset.readingText || "Reading the bid…")
        : (el.dataset.filledText || "Filled in from the bid — double-check the fields below.")
      el.classList.remove("hidden")
    })
  }

  _showAiBidChip(name) {
    if (!this.hasAiBidChipTarget) return
    this.aiBidPromptTarget.classList.add("hidden")
    this.aiBidChip = name
    this.aiBidChipTarget.classList.remove("hidden")
    this.aiBidChipTarget.innerHTML = ""
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-purple-100 text-purple-700 font-medium"
    chip.textContent = `📄 ${name} — AI will draft from this bid`
    const x = document.createElement("button")
    x.type = "button"
    x.textContent = "×"
    x.className = "text-gray-400 hover:text-red-600 cursor-pointer font-medium ml-1"
    x.addEventListener("click", (ev) => {
      ev.stopPropagation()
      this.aiBidInputTarget.value = ""
      this.aiBidChipTarget.classList.add("hidden")
      this.aiBidPromptTarget.classList.remove("hidden")
      this._cancelPeekAiBid()
    })
    chip.appendChild(x)
    this.aiBidChipTarget.appendChild(chip)
  }

  // Contract type changed in the New Contract popup: pre-select whatever
  // template the user last picked for THAT type (Phelipe 2026-07-24). Types
  // never used before fall back to "no template".
  typeChanged(e) {
    // A pick the user made themselves wins over any later bid-peek suggestion.
    if (!this._applyingPeek) this._userPickedType = true
    // Retainage only applies to the GC-facing and sub-facing agreements.
    if (this.hasRetainageWrapTarget) {
      const t = e.currentTarget.value
      this.retainageWrapTarget.classList.toggle("hidden", !(t === "Owner ↔ GC" || t === "Subcontractor"))
    }
    if (!this.hasTemplateFieldTarget) return
    let memory = {}
    try { memory = JSON.parse(e.currentTarget.dataset.templateMemory || "{}") } catch (_) { memory = {} }
    const want = (memory[e.currentTarget.value] || "").toString()
    const field = this.templateFieldTarget
    field.value = [...field.options].some(o => o.value === want) ? want : ""
  }

  setSource(e) {
    const src = e.currentTarget.dataset.source
    this.element.querySelectorAll("[data-source-tab]").forEach(b => this._sourceCard(b, b.dataset.source === src))
    this.element.querySelectorAll("[data-source-panel]").forEach(p => p.classList.toggle("hidden", p.dataset.sourcePanel !== src))
    // Submit the picked source with the form (so "ai" actually generates).
    if (this.hasSourceFieldTarget) this.sourceFieldTarget.value = src
    this._clearUploadError()
    this._paintCreateBtn()
  }

  // ── New Contract validation ──
  // "Create & open" with the Upload source needs a PDF first: block the
  // submit and say so (or point at the Template / AI options instead).
  validateNew(e) {
    // Already submitting — swallow repeat clicks/Enter presses.
    if (this._submitting) {
      e.preventDefault()
      return
    }

    // AI is still reading the dropped bid (the button shows it) — an Enter
    // press would sneak past the disabled button, so hold the submit too.
    if (this._peeking) {
      e.preventDefault()
      return
    }

    const source = this.hasSourceFieldTarget ? this.sourceFieldTarget.value : "upload"
    const needsFile = (this._dir || "sending") === "receiving" || source === "upload"

    if (needsFile) {
      if (this._uploading) {
        e.preventDefault()
        this._showUploadError("Hang on — your PDF is still uploading.")
        return
      }
      const hasFile = this.hasUploadedPdfFieldTarget && this.uploadedPdfFieldTarget.value.trim() !== ""
      if (!hasFile) {
        e.preventDefault()
        const msg = (this._dir || "sending") === "receiving"
          ? "Upload the contract you received to continue."
          : "Upload a PDF, or switch to Generate with AI."
        this._showUploadError(msg)
        return
      }
    }

    // Duplicate gate (Phelipe 2026-08-19): before the form goes, ask the
    // server whether this counterparty already exists in the company
    // directory (loose match + one AI pass). On a hit the GC picks reuse
    // vs truly-new; either answer rides hidden fields into create. Checked
    // once per submit; any failure lets the form through.
    if (!this._dupChecked && this.hasDupModalTarget) {
      const cpInput = this.hasCounterpartyInputTarget ? this.counterpartyInputTarget : null
      const cpName = (cpInput?.value || "").trim()
      const url = this.dupModalTarget.dataset.dupCheckUrl
      if (cpName && url && !(this.hasDupCompanyFieldTarget && this.dupCompanyFieldTarget.value) &&
          !(this.hasDupForceFieldTarget && this.dupForceFieldTarget.value)) {
        e.preventDefault()
        this._setCreating(true)
        this._checkCounterpartyDup(e.target, cpName, url)
        return
      }
      this._dupChecked = true
    }

    // Validation passed — the form is on its way. Lock the button and show
    // a spinner so the user sees the click landed (AI drafts take a moment).
    this._setCreating(true)
  }

  async _checkCounterpartyDup(form, name, url) {
    let dup = null
    try {
      const resp = await fetch(url, { method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ name }) })
      dup = (await resp.json())?.duplicate
    } catch { dup = null }
    this._dupChecked = true
    // Drop the submitting lock before re-submitting — validateNew's repeat
    // guard would swallow the requestSubmit otherwise.
    if (!dup) { this._setCreating(false); form.requestSubmit(); return }

    // Show the pick — the submit continues from the modal buttons.
    this._setCreating(false)
    this._dupForm = form
    this._dupId = dup.id
    this.dupNewNameTarget.textContent = name
    this.dupExistingNameTarget.textContent = dup.name
    const bits = [`${dup.projects || 0} project${dup.projects === 1 ? "" : "s"}`]
    if (dup.docs > 0) bits.push(`${dup.docs} document${dup.docs === 1 ? "" : "s"} on file`)
    if (dup.contact) bits.push(dup.contact)
    this.dupExistingMetaTarget.textContent = bits.join(" · ")
    this.dupModalTarget.classList.remove("hidden")
  }

  dupUseExisting() {
    if (this.hasDupCompanyFieldTarget) this.dupCompanyFieldTarget.value = this._dupId || ""
    this._resumeAfterDup()
  }

  dupCreateNew() {
    if (this.hasDupForceFieldTarget) this.dupForceFieldTarget.value = "1"
    this._resumeAfterDup()
  }

  closeDupModal() {
    if (this.hasDupModalTarget) this.dupModalTarget.classList.add("hidden")
    // Cancel means "let me look again" — next submit re-checks.
    this._dupChecked = false
    this._setCreating(false)
  }

  _resumeAfterDup() {
    if (this.hasDupModalTarget) this.dupModalTarget.classList.add("hidden")
    this._setCreating(false)
    this._dupForm?.requestSubmit()
  }

  _setCreating(on) {
    this._submitting = !!on
    if (!this.hasCreateBtnTarget) return
    const btn = this.createBtnTarget
    if (on) {
      this._createBtnHtml = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> Creating…`
    } else {
      btn.disabled = false
      if (this._createBtnHtml) btn.innerHTML = this._createBtnHtml
    }
  }

  _showUploadError(msg) {
    this.uploadErrorTargets.forEach(el => {
      el.textContent = msg
      el.classList.remove("hidden")
    })
    this.uploadZoneTargets.forEach(z => {
      z.classList.remove("border-gray-300")
      z.classList.add("border-red-400", "bg-red-50/40")
    })
  }

  _clearUploadError() {
    if (!this.hasUploadErrorTarget) return
    this.uploadErrorTargets.forEach(el => el.classList.add("hidden"))
    this.uploadZoneTargets.forEach(z => {
      z.classList.remove("border-red-400", "bg-red-50/40")
      z.classList.add("border-gray-300")
    })
  }

  // ── PDF picking + direct-to-S3 upload (presign → PUT) ──
  browsePdf() {
    if (this.hasPdfInputTarget) this.pdfInputTarget.click()
  }

  dragOverPdf(e) {
    e.preventDefault()
    // The AI zone highlights in its own purple family; the plain zone
    // keeps the brand highlight (Phelipe 2026-08-26).
    const purple = e.currentTarget.dataset.aiZone === "1"
    e.currentTarget.classList.add(...(purple ? ["border-purple-500", "bg-purple-100"] : ["border-brand", "bg-brand/5"]))
  }

  dragLeavePdf(e) {
    e.currentTarget.classList.remove("border-brand", "bg-brand/5", "border-purple-500", "bg-purple-100")
  }

  dropPdf(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("border-brand", "bg-brand/5", "border-purple-500", "bg-purple-100")
    const file = e.dataTransfer?.files?.[0]
    if (file) this._uploadPdf(file)
  }

  pdfPicked(e) {
    const file = e.currentTarget.files?.[0]
    if (file) this._uploadPdf(file)
    e.currentTarget.value = ""
  }

  async _uploadPdf(file) {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      this._showUploadError("That file isn't a PDF — contracts must be uploaded as PDF.")
      return
    }
    this._clearUploadError()
    // Received contracts prefill too (Larry 2026-08-26): the drop IS the
    // document, so skim it for name / type / counterparty while the file
    // uploads — same peek the bid box uses, received-contract prompt.
    if (this.hasDirectionFieldTarget && this.directionFieldTarget.value === "receiving") {
      this._peekAiBid(file, "received")
    } else if (this.hasSourceFieldTarget && this.sourceFieldTarget.value === "upload") {
      // Sending → Upload too (Phelipe 2026-10-23): same skim, a sent-contract
      // prompt (the counterparty is the other party). No AI → no peek URL →
      // _peekAiBid returns quietly and it's a plain upload.
      this._peekAiBid(file, "sent")
    }
    this._uploading = true
    this._setChips(`<span class="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[13px] font-medium">
        <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
        ${this._esc(file.name)}</span>`)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const presignResp = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ files: [{ filename: file.name, content_type: "application/pdf", byte_size: file.size }] })
      })
      const presign = await presignResp.json()
      if (!presignResp.ok || !presign.uploads?.[0]) throw new Error(presign.error || "presign failed")

      const { upload_url, cache_name } = presign.uploads[0]
      const putResp = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": "application/pdf" }, body: file })
      if (!putResp.ok) throw new Error(`S3 PUT ${putResp.status}`)

      this.uploadedPdfFieldTarget.value = cache_name
      this._setChips(`<span class="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-[13px] font-medium">
          <svg class="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>
          ${this._esc(file.name)}
          <button type="button" data-action="click->contracts#removePdf" class="text-gray-400 hover:text-red-500 font-bold leading-none" title="Remove file">&times;</button></span>`)
    } catch (err) {
      console.error("[contracts] PDF upload failed:", err)
      this.uploadedPdfFieldTarget.value = ""
      this._resetChips()
      this._showUploadError("Upload failed — please try again.")
    } finally {
      this._uploading = false
    }
  }

  removePdf(e) {
    e.stopPropagation()
    this.uploadedPdfFieldTarget.value = ""
    this._resetChips()
  }

  _setChips(html) {
    this.uploadPromptTargets.forEach(p => p.classList.add("hidden"))
    this.uploadChipTargets.forEach(c => { c.innerHTML = html; c.classList.remove("hidden") })
  }

  _resetChips() {
    this.uploadPromptTargets.forEach(p => p.classList.remove("hidden"))
    this.uploadChipTargets.forEach(c => { c.innerHTML = ""; c.classList.add("hidden") })
  }

  _esc(s) {
    const d = document.createElement("div")
    d.textContent = s
    return d.innerHTML
  }

  pickAction(e) {
    if (this.hasRxActionFieldTarget) {
      const opt = e.currentTarget.closest("[data-rx-action]")
      if (opt) this.rxActionFieldTarget.value = opt.dataset.rxAction
    }
    this.element.querySelectorAll("[data-upload-opt]").forEach(o => {
      o.classList.remove("border-brand", "bg-brand/5"); o.classList.add("border-gray-200")
    })
    const lbl = e.currentTarget
    lbl.classList.remove("border-gray-200"); lbl.classList.add("border-brand", "bg-brand/5")
    const radio = lbl.querySelector("input[type=radio]"); if (radio) radio.checked = true
    this._paintCreateBtn()
  }

  // ── Row navigation ──
  openRow(e) {
    const href = e.currentTarget.dataset.href
    if (href) window.location = href
  }

  // ── Filter tabs + search ──
  filter(e) {
    const cat = e.currentTarget.dataset.category
    this.filterTabTargets.forEach(t => this._tab(t, t.dataset.category === cat))
    this._cat = cat
    this._apply()
  }
  search() { this._apply() }
  _apply() {
    const cat = this._cat || "all"
    const q = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()
    let visible = 0
    this.rowTargets.forEach(r => {
      const show = (cat === "all" || r.dataset.category === cat) && (!q || (r.dataset.search || "").includes(q))
      r.classList.toggle("hidden", !show)
      if (show) visible++
    })
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", visible > 0)
  }

  // ── Row-menu actions ──
  async reminder(e) {
    e.stopPropagation()
    this._closeMenus()
    const url = e.currentTarget.dataset.remindUrl
    if (!url) return
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        this._toast(data.count > 0 ? `Reminder emailed to ${data.count} ${data.count === 1 ? "signer" : "signers"}` : "No pending signers with an email to remind")
      } else {
        this._toast(data.message || "Couldn't send reminders.", "error")
      }
    } catch (_) {
      this._toast("Couldn't send reminders.", "error")
    }
  }

  downloadPdf(e) {
    e.stopPropagation()
    this._closeMenus()
    const url = e.currentTarget.dataset.downloadUrl
    if (url) window.open(url, "_blank")
  }

  copyLink(e) {
    e.stopPropagation()
    this._closeMenus()
    const url = e.currentTarget.dataset.shareUrl
    if (!url) return
    navigator.clipboard?.writeText(url)
      .then(() => this._toast("Contract link copied"))
      .catch(() => this._toast("Couldn't copy the link.", "error"))
  }

  fromTemplate(e) { if (e) e.preventDefault(); this.openNew(e) }
  comingSoon(e) { this._act(e, "Coming soon") }

  // ── Manage Templates page (real company templates) ──
  openNewTemplate(e) {
    if (e) e.preventDefault()
    if (this.hasTemplateNameTarget) this.templateNameTarget.value = ""
    if (this.hasTemplateDescriptionTarget) this.templateDescriptionTarget.value = ""
    if (this.hasNewTemplateModalTarget) this.newTemplateModalTarget.classList.remove("hidden")
  }
  closeNewTemplate(e) {
    if (this.hasNewTemplateModalTarget) this.newTemplateModalTarget.classList.add("hidden")
  }

  async submitNewTemplate() {
    const name = this.hasTemplateNameTarget ? this.templateNameTarget.value.trim() : ""
    if (!name) { this.templateNameTarget?.focus(); return }
    const btn = this.hasTemplateSubmitBtnTarget ? this.templateSubmitBtnTarget : null
    if (btn) { btn.disabled = true; btn.classList.add("opacity-60") }
    try {
      const resp = await fetch(this.createTemplateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin",
        body: JSON.stringify({
          name: name,
          contract_type: this.hasTemplateTypeTarget ? this.templateTypeTarget.value : "",
          base: this._baseKeyForType(this.hasTemplateTypeTarget ? this.templateTypeTarget.value : ""),
          description: this.hasTemplateDescriptionTarget ? this.templateDescriptionTarget.value.trim() : ""
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        if (data.row && this.hasTemplatesBodyTarget) this.templatesBodyTarget.insertAdjacentHTML("afterbegin", data.row)
        this._refreshTemplatesEmpty()
        this.closeNewTemplate()
        this._toast("Template created")
      } else {
        this._toast(data.message || "Couldn't create the template.", "error")
      }
    } catch (_) {
      this._toast("Couldn't create the template.", "error")
    } finally {
      if (btn) { btn.disabled = false; btn.classList.remove("opacity-60") }
    }
  }

  openRenameTemplate(e) {
    e.stopPropagation()
    this._closeMenus()
    this._renameTemplateId = e.currentTarget.dataset.templateId
    this._renameTemplateRow = e.currentTarget.closest("tr")
    if (this.hasRenameTemplateNameTarget) this.renameTemplateNameTarget.value = e.currentTarget.dataset.templateName || ""
    if (this.hasRenameTemplateModalTarget) this.renameTemplateModalTarget.classList.remove("hidden")
  }
  closeRenameTemplate(e) {
    if (this.hasRenameTemplateModalTarget) this.renameTemplateModalTarget.classList.add("hidden")
  }

  async submitRenameTemplate() {
    const name = this.hasRenameTemplateNameTarget ? this.renameTemplateNameTarget.value.trim() : ""
    if (!name || !this._renameTemplateId) return
    try {
      const resp = await fetch(this.renameTemplateUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin",
        body: JSON.stringify({ template_id: this._renameTemplateId, name: name })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        const cell = this._renameTemplateRow?.querySelector("[data-template-name-cell]")
        if (cell) cell.textContent = name
        this._renameTemplateRow?.querySelectorAll("[data-template-name]").forEach(el => { el.dataset.templateName = name })
        this.closeRenameTemplate()
        this._toast("Template renamed")
      } else {
        this._toast(data.message || "Couldn't rename the template.", "error")
      }
    } catch (_) {
      this._toast("Couldn't rename the template.", "error")
    }
  }

  async duplicateTemplate(e) {
    e.stopPropagation()
    this._closeMenus()
    try {
      const resp = await fetch(this.duplicateTemplateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin",
        body: JSON.stringify({ template_id: e.currentTarget.dataset.templateId })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        if (data.row && this.hasTemplatesBodyTarget) this.templatesBodyTarget.insertAdjacentHTML("afterbegin", data.row)
        this._refreshTemplatesEmpty()
        this._toast("Template duplicated")
      } else {
        this._toast(data.message || "Couldn't duplicate the template.", "error")
      }
    } catch (_) {
      this._toast("Couldn't duplicate the template.", "error")
    }
  }

  openDeleteTemplate(e) {
    e.stopPropagation()
    this._closeMenus()
    this._deleteTemplateId = e.currentTarget.dataset.templateId
    this._deleteTemplateRow = e.currentTarget.closest("tr")
    if (this.hasDeleteTemplateNameTarget) this.deleteTemplateNameTarget.textContent = e.currentTarget.dataset.templateName || "this template"
    if (this.hasDeleteTemplateModalTarget) this.deleteTemplateModalTarget.classList.remove("hidden")
  }
  closeDeleteTemplate(e) {
    if (this.hasDeleteTemplateModalTarget) this.deleteTemplateModalTarget.classList.add("hidden")
    this._deleteTemplateId = null
    this._deleteTemplateRow = null
  }

  async confirmDeleteTemplate() {
    if (!this._deleteTemplateId) return
    try {
      const resp = await fetch(this.deleteTemplateUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin",
        body: JSON.stringify({ template_id: this._deleteTemplateId })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        this._deleteTemplateRow?.remove()
        this._refreshTemplatesEmpty()
        this.closeDeleteTemplate()
        this._toast("Template deleted")
      } else {
        this.closeDeleteTemplate()
        this._toast(data.message || "Couldn't delete the template.", "error")
      }
    } catch (_) {
      this.closeDeleteTemplate()
      this._toast("Couldn't delete the template.", "error")
    }
  }

  _refreshTemplatesEmpty() {
    if (!this.hasTemplatesEmptyTarget) return
    this.templatesEmptyTarget.classList.toggle("hidden", this.templateRowTargets.length > 0)
  }

  // Map a display type to the built-in boilerplate key used as the body seed.
  _baseKeyForType(type) {
    const t = (type || "").toLowerCase()
    if (t.includes("owner")) return "owner_gc"
    if (t.includes("supplier")) return "supplier"
    if (t.includes("equipment")) return "equipment"
    return "subcontractor"
  }

  // ── Delete contract (confirmation modal → DELETE → remove row) ──
  removeContract(e) {
    e.stopPropagation()
    this._closeMenus()
    const btn = e.currentTarget
    this._deleteId = btn.dataset.contractId
    this._deleteRow = btn.closest("tr")
    if (this.hasDeleteNameTarget) this.deleteNameTarget.textContent = btn.dataset.contractName || "this contract"
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.remove("hidden")
  }

  closeDeleteModal() {
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.add("hidden")
    if (this.hasDeleteNotifyTarget) this.deleteNotifyTarget.checked = false
    this._deleteId = null
    this._deleteRow = null
  }

  async confirmDelete() {
    const id = this._deleteId
    const row = this._deleteRow
    if (!id) return

    const btn = this.hasDeleteConfirmBtnTarget ? this.deleteConfirmBtnTarget : null
    if (btn) { btn.disabled = true; btn.classList.add("opacity-60") }

    try {
      let url = this.deleteUrlTemplateValue.replace(/\/0(\/|$)/, `/${id}$1`)
      if (this.hasDeleteNotifyTarget && this.deleteNotifyTarget.checked) {
        url += (url.includes("?") ? "&" : "?") + "notify_counterparty=1"
      }
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(url, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))

      if (resp.ok && data.ok) {
        row?.remove()
        if (data.trash_row && this.hasTrashBodyTarget) {
          this.trashBodyTarget.insertAdjacentHTML("afterbegin", data.trash_row)
        }
        this._refreshTrashState()
        this._bumpTotalCount(-1)
        this.closeDeleteModal()
        this._toast("Contract moved to Trash")
        this._apply() // refresh the empty state for the current filter
      } else {
        this.closeDeleteModal()
        this._toast(data.message || "Could not delete the contract.", "error")
      }
    } catch (err) {
      console.error("[contracts] delete failed:", err)
      this.closeDeleteModal()
      this._toast("Could not delete the contract.", "error")
    } finally {
      if (btn) { btn.disabled = false; btn.classList.remove("opacity-60") }
    }
  }

  // The "Total contracts" number lives in three places (header pill, summary
  // card, All tab) — nudge them all on delete/restore so the page never needs
  // a refresh to agree with itself (Larry 2026-08-20).
  _bumpTotalCount(delta) {
    this.totalCountTargets.forEach(el => {
      const n = parseInt(el.textContent.trim(), 10)
      if (!Number.isNaN(n)) el.textContent = Math.max(0, n + delta)
    })
  }

  // ── Trash (soft-deleted contracts: toggle panel, restore, delete forever) ──
  toggleTrash() {
    if (!this.hasTrashSectionTarget) return
    const nowHidden = this.trashSectionTarget.classList.toggle("hidden")
    if (this.hasTrashToggleTarget) {
      this.trashToggleTarget.classList.toggle("bg-gray-100", !nowHidden)
      this.trashToggleTarget.classList.toggle("text-gray-700", !nowHidden)
    }
    if (!nowHidden) this.trashSectionTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  async restoreContract(e) {
    e.stopPropagation()
    const btn = e.currentTarget
    const id = btn.dataset.contractId
    const row = btn.closest("tr")
    btn.disabled = true
    btn.classList.add("opacity-60")

    try {
      const url = this.deleteUrlTemplateValue.replace(/\/0(\/|$)/, `/${id}$1`) + "/restore"
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))

      if (resp.ok && data.ok) {
        row?.remove()
        if (data.row && this.hasTableBodyTarget) {
          this.tableBodyTarget.insertAdjacentHTML("afterbegin", data.row)
        }
        this._refreshTrashState()
        this._bumpTotalCount(1)
        this._toast("Contract restored")
        this._apply() // the restored row obeys the active filter/search
      } else {
        this._toast(data.message || "Could not restore the contract.", "error")
      }
    } catch (err) {
      console.error("[contracts] restore failed:", err)
      this._toast("Could not restore the contract.", "error")
    } finally {
      btn.disabled = false
      btn.classList.remove("opacity-60")
    }
  }

  removeForever(e) {
    e.stopPropagation()
    const btn = e.currentTarget
    this._deleteForeverId = btn.dataset.contractId
    this._deleteForeverRow = btn.closest("tr")
    if (this.hasDeleteForeverNameTarget) this.deleteForeverNameTarget.textContent = btn.dataset.contractName || "this contract"
    if (this.hasDeleteForeverModalTarget) this.deleteForeverModalTarget.classList.remove("hidden")
  }

  closeDeleteForeverModal() {
    if (this.hasDeleteForeverModalTarget) this.deleteForeverModalTarget.classList.add("hidden")
    this._deleteForeverId = null
    this._deleteForeverRow = null
  }

  async confirmDeleteForever() {
    const id = this._deleteForeverId
    const row = this._deleteForeverRow
    if (!id) return

    const btn = this.hasDeleteForeverBtnTarget ? this.deleteForeverBtnTarget : null
    if (btn) { btn.disabled = true; btn.classList.add("opacity-60") }

    try {
      const url = this.deleteUrlTemplateValue.replace(/\/0(\/|$)/, `/${id}$1`) + "?permanent=1"
      const resp = await fetch(url, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))

      if (resp.ok && data.ok) {
        row?.remove()
        this._refreshTrashState()
        this.closeDeleteForeverModal()
        this._toast("Contract permanently deleted")
      } else {
        this.closeDeleteForeverModal()
        this._toast(data.message || "Could not delete the contract.", "error")
      }
    } catch (err) {
      console.error("[contracts] permanent delete failed:", err)
      this.closeDeleteForeverModal()
      this._toast("Could not delete the contract.", "error")
    } finally {
      if (btn) { btn.disabled = false; btn.classList.remove("opacity-60") }
    }
  }

  _refreshTrashState() {
    const count = this.trashRowTargets.length
    if (this.hasTrashCountTarget) this.trashCountTarget.textContent = count
    if (this.hasTrashEmptyTarget) this.trashEmptyTarget.classList.toggle("hidden", count > 0)
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  toast(e) { if (e) e.stopPropagation(); this._closeMenus(); this._toast(e.currentTarget.dataset.toast || "Done") }

  _act(e, msg, type) {
    if (e) e.stopPropagation()
    this._closeMenus()
    this._toast(msg, type)
  }

  // Close any open row dropdown after an action runs.
  _closeMenus() { document.dispatchEvent(new CustomEvent("dropdown:close")) }

  // The Sending source choice (Generate with AI / Upload) is a selectable
  // card, not a pill (Phelipe 2026-10-23: "make it obvious when I'm clicking
  // on one") — mirrors the classes _new_contract_modal renders.
  _sourceCard(el, on) {
    el.setAttribute("aria-checked", on ? "true" : "false")
    el.classList.toggle("border-brand", on)
    el.classList.toggle("bg-brand/5", on)
    el.classList.toggle("border-gray-200", !on)
    el.classList.toggle("bg-white", !on)
    el.classList.toggle("hover:border-gray-300", !on)
    el.classList.toggle("hover:bg-gray-50", !on)
    const title = el.querySelector("[data-card-title]")
    if (title) {
      title.classList.toggle("text-brand", on)
      title.classList.toggle("text-gray-700", !on)
    }
    const radio = el.querySelector("[data-card-radio]")
    if (radio) {
      radio.classList.toggle("border-brand", on)
      radio.classList.toggle("bg-brand", on)
      radio.classList.toggle("border-gray-300", !on)
      radio.classList.toggle("bg-white", !on)
    }
    el.querySelector("[data-card-radio-dot]")?.classList.toggle("hidden", !on)
  }

  // styling helper for pill/tab toggles
  _tab(el, on) {
    el.classList.toggle("bg-white", on)
    el.classList.toggle("shadow-sm", on)
    el.classList.toggle("font-medium", on)
    el.classList.toggle("text-gray-800", on)
    el.classList.toggle("text-gray-600", !on)
  }

  _toast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const p = document.createElement("p")
    p.className = "flex-1 text-sm"
    p.textContent = message
    el.appendChild(p)
    document.body.prepend(el)
  }
}
